/**
 * DatabaseTab — Server database config table with validation and fix controls.
 *
 * Displays:
 *   - DB existence badge (from ping result)
 *   - Action bar: Validate, Fix (conditional), Edit Config buttons
 *   - Validation results (inline)
 *   - Searchable, sortable, paginated config table
 */

import { useState, useMemo, useCallback } from 'react';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';

const DatabaseTab = ({
  configData, dbValid, editRights,
  onEditRow, onValidate, onFix, loading,
}) => {
  // Search
  const [search, setSearch] = useState('');

  // Sorting
  const [sortCol, setSortCol] = useState('config_name');
  const [sortDir, setSortDir] = useState('asc');

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // Validation state
  const [validating, setValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  const dbExists = dbValid?.valid === true || dbValid?.valid === 1;

  // Filter + sort + paginate
  const filtered = useMemo(() => {
    let rows = [...(configData || [])];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.config_name || '').toLowerCase().includes(q) ||
        (r.config_value || '').toString().toLowerCase().includes(q) ||
        (r.config_description || '').toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => {
      const aVal = (a[sortCol] || '').toString().toLowerCase();
      const bVal = (b[sortCol] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [configData, search, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortCol]);

  const handleValidate = useCallback(async () => {
    setValidating(true);
    const data = await onValidate();
    setValidationResults(data);
    setValidating(false);
  }, [onValidate]);

  const handleFix = useCallback(async () => {
    if (!validationResults) return;
    setValidating(true);
    const data = await onFix(validationResults.missing || [], validationResults.extras || []);
    setValidationResults(data);
    setValidating(false);
  }, [onFix, validationResults]);

  const hasMissing = validationResults?.missing?.length > 0;
  const hasExtra = validationResults?.extras?.length > 0;
  const hasDupes = validationResults?.dupes?.length > 0;
  const isValid = validationResults?.validated && !hasMissing && !hasExtra;

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <>
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Database Configuration</h3>
          <div className="pp-section__actions">
            {/* DB existence badge */}
            {dbValid && (
              <span className={`sp-validation__badge sp-validation__badge--${dbExists ? 'exists' : 'missing'}`}>
                {dbExists ? '● DB Found' : '○ DB Not Found'}
              </span>
            )}
          </div>
        </div>

        <div className="pp-section__body">
          {/* Action bar */}
          <div className="sp-validation__bar">
            <button
              className="dp-btn dp-btn--secondary dp-btn--sm"
              onClick={handleValidate}
              disabled={validating || !dbExists}
            >
              {validating ? 'Validating…' : 'Validate'}
            </button>
            {validationResults && (hasMissing || hasExtra) && editRights && (
              <button
                className="dp-btn dp-btn--primary dp-btn--sm"
                onClick={handleFix}
                disabled={validating}
              >
                Fix Database
              </button>
            )}
          </div>

          {/* Inline validation results */}
          {validationResults?.validated && (hasMissing || hasExtra || hasDupes) && (
            <div className="sp-validation__results">
              <h4 className="sp-validation__results-title sp-validation__results-title--invalid">Database Invalid:</h4>
              <ul className="sp-validation__results-list">
                {validationResults.missing?.map((name, idx) => (
                  <li key={`missing-${idx}`} className="sp-validation__results-item sp-validation__results-item--missing">
                    ✗ {name} — Missing Requirement
                  </li>
                ))}
                {validationResults.extras?.map((name, idx) => (
                  <li key={`extra-${idx}`} className="sp-validation__results-item sp-validation__results-item--extra">
                    ⚠ {name} — Extra Config
                  </li>
                ))}
                {validationResults.dupes?.map((name, idx) => (
                  <li key={`dupe-${idx}`} className="sp-validation__results-item sp-validation__results-item--extra">
                    ⚠ {name} — Duplicate Config
                  </li>
                ))}
              </ul>
            </div>
          )}
          {validationResults?.validated && isValid && (
            <div className="sp-validation__results">
              <p className="sp-validation__results-title" style={{ color: 'var(--color-success-text)' }}>
                ✓ Database is valid — no missing or extra config items.
              </p>
            </div>
          )}

          {/* Search */}
          <div className="dp-controls" style={{ marginTop: 'var(--space-4)' }}>
            <div className="dp-controls__left">
              <div className="dp-search">
                <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                  className="dp-search__input"
                  type="text"
                  placeholder="Search config…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </div>
            </div>
          </div>

          {/* Config table */}
          {configData.length === 0 && !loading ? (
            <div className="dp-empty">
              <p className="dp-empty__title">No configuration data</p>
              <p className="dp-empty__text">Database configuration is not available for this server.</p>
            </div>
          ) : (
            <>
              <div className="dp-table-wrap">
                <table className="dp-table">
                  <thead className="dp-thead">
                    <tr>
                      {editRights && (
                        <th className="dp-th dp-th--actions">Edit</th>
                      )}
                      <th className="dp-th">
                        <div className="dp-th__content">
                          <button className="dp-th__sort-btn" onClick={() => handleSort('config_name')}>
                            Config Name <SortIcon col="config_name" />
                          </button>
                        </div>
                      </th>
                      <th className="dp-th">
                        <div className="dp-th__content">
                          <button className="dp-th__sort-btn" onClick={() => handleSort('config_value')}>
                            Value <SortIcon col="config_value" />
                          </button>
                        </div>
                      </th>
                      <th className="dp-th">
                        <div className="dp-th__content">
                          <button className="dp-th__sort-btn" onClick={() => handleSort('data_type')}>
                            Data Type <SortIcon col="data_type" />
                          </button>
                        </div>
                      </th>
                      <th className="dp-th">
                        <div className="dp-th__content">
                          <button className="dp-th__sort-btn" onClick={() => handleSort('description')}>
                            Description <SortIcon col="description" />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, idx) => (
                      <tr key={row.config_id || idx} className="dp-tr">
                        {editRights && (
                          <td className="dp-td dp-td--actions">
                            <button
                              type="button"
                              className="dp-row-actions__trigger sp-config-edit-btn"
                              onClick={() => onEditRow(row)}
                              aria-label={`Edit ${row.config_name}`}
                              title="Edit value"
                            >
                              <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.931-8.93z" />
                              </svg>
                            </button>
                          </td>
                        )}
                        <td className="dp-td">
                          <span className="dp-cell-text">{row.config_name}</span>
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text">{row.config_value}</span>
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text dp-cell-text--muted">{row.data_type}</span>
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text dp-cell-text--secondary">{row.description}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filtered.length > perPage && (
                <div className="dp-pagination">
                  <span className="dp-pagination__info">
                    Showing <span className="dp-pagination__emphasis">{(page - 1) * perPage + 1}</span>
                    –<span className="dp-pagination__emphasis">{Math.min(page * perPage, filtered.length)}</span>
                    {' '}of <span className="dp-pagination__emphasis">{filtered.length}</span>
                  </span>
                  <div className="dp-pagination__controls">
                    <div className="dp-per-page">
                      <span className="dp-per-page__label">Per page:</span>
                      <select className="dp-per-page__select" value={perPage} onChange={(e) => { setPerPage(+e.target.value); setPage(1); }}>
                        {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="dp-page-nav">
                      <button className="dp-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                        Math.max(0, page - 3), Math.min(totalPages, page + 2)
                      ).map((p) => (
                        <button
                          key={p}
                          className={`dp-page-num${p === page ? ' is-active' : ''}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      ))}
                      <button className="dp-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DatabaseTab;
